
/**
 * 缓存键前缀
 */
const CACHE_KEY_PREFIX = 'exchange_rate_'

/**
 * 缓存有效期（毫秒）- 1 小时
 */
const CACHE_EXPIRY_TIME = 60 * 60 * 1000

/**
 * 从缓存中获取汇率
 * @param {string} currencyCode - 货币代码，如 'USD', 'THB' 等
 * @returns {number|null} 返回缓存的汇率，如果缓存不存在或已过期则返回 null
 */
export function getExchangeRateFromCache(currencyCode) {
  if (!currencyCode) return null

  const cacheKey = `${CACHE_KEY_PREFIX}${currencyCode}`
  const cacheData = localStorage.getItem(cacheKey)

  if (!cacheData) {
    return null
  }

  try {
    const { rate, timestamp } = JSON.parse(cacheData)
    const now = Date.now()

    // 检查缓存是否过期（1 小时）
    if (now - timestamp > CACHE_EXPIRY_TIME) {
      // 缓存已过期，清除缓存
      localStorage.removeItem(cacheKey)
      return null
    }

    return rate
  } catch (e) {
    // 解析失败，清除缓存
    localStorage.removeItem(cacheKey)
    return null
  }
}

/**
 * 将汇率存入缓存
 * @param {string} currencyCode - 货币代码，如 'USD', 'THB' 等
 * @param {number} rate - 汇率值
 */
export function saveExchangeRateToCache(currencyCode, rate) {
  if (!currencyCode || rate === undefined || rate === null) return

  const cacheKey = `${CACHE_KEY_PREFIX}${currencyCode}`
  const cacheData = JSON.stringify({
    rate: rate,
    timestamp: Date.now()
  })

  try {
    localStorage.setItem(cacheKey, cacheData)
  } catch (e) {
    console.error('保存汇率到缓存失败:', e)
  }
}

/**
 * 获取汇率（优先从缓存读取，缓存失效时调用 API）
 * @param {string} currencyCode - 货币代码，如 'USD', 'THB' 等
 * @param {Function} apiMethod - 获取汇率的 API 方法，应该返回 Promise
 * @returns {Promise<number>} 返回汇率值
 */
export async function getExchangeRate(currencyCode, apiMethod) {
  if (!currencyCode) {
    throw new Error('货币代码不能为空')
  }

  // 1. 先尝试从缓存获取
  const cachedRate = getExchangeRateFromCache(currencyCode)
  if (cachedRate !== null) {
    console.log(`从缓存获取 ${currencyCode} 汇率:`, cachedRate)
    return cachedRate
  }

  // 2. 缓存不存在或已过期，调用 API 获取
  console.log(`从 API 获取 ${currencyCode} 汇率`)
  try {
    const response = await apiMethod(currencyCode)

    // 假设 API 返回的数据结构为 { code: 1000, data: { rate: 0.5 } }
    // 根据实际情况调整
    let rate
    if (response && response.code === 1000) {
      rate = response.data?.rate || response.data?.exchangeRate || response.exchangeRate
    } else if (typeof response === 'number') {
      rate = response
    } else {
      rate = response?.rate || response?.exchangeRate
    }

    if (!rate || isNaN(rate)) {
      throw new Error('获取到的汇率无效')
    }

    // 3. 保存到缓存
    saveExchangeRateToCache(currencyCode, rate)

    return rate
  } catch (error) {
    console.error('获取汇率失败:', error)
    throw error
  }
}

/**
 * 货币格式化方法
 * @param {number} amount - 金额
 * @param {number} decimals - 小数位数，默认 2 位
 * @returns {string} 格式化后的金额字符串
 */
export function formatMoney(amount, decimals = 2) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-'
  }
  const num = Number(amount)
  return num.toFixed(decimals).replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,')
}

/**
 * 汇率转换方法
 * @param {number} amount - 原始金额
 * @param {number} exchangeRate - 汇率
 * @param {number} decimals - 小数位数，默认 2 位
 * @returns {string} 转换并格式化后的金额字符串
 */
export function convertCurrency(amount, exchangeRate, decimals = 2) {

  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-'
  }
  if (!exchangeRate || exchangeRate === 0) {
    return formatMoney(amount, decimals)
  }
  const convertedAmount = Number(amount) * exchangeRate
  return formatMoney(convertedAmount, decimals)
}

/**
 * 带汇率的金额渲染方法（用于表格列的 customRender）
 * @param {number} text - 金额值
 * @param {number} exchangeRate - 汇率
 * @param {number} decimals - 小数位数，默认 2 位
 * @returns {string} 格式化后的金额字符串
 */
export function renderConvertedAmount(text, exchangeRate, decimals = 2) {
  if (!text && text !== 0) return '-'
  return convertCurrency(text, exchangeRate, decimals)
}
